import { useState } from "react";
import { Apis } from "../../lib/Apis";
import Swal from "sweetalert2";

// --- Hook from Brands/useHook.js ---
export const useBrandHook = () => {
  const addBrand = async (data) => {
    try {
      const response = await Apis.addBrand(data);
      return response;
    } catch (error) {
      return error;
    }
  };

  const getBrands = async (setBrands) => {
    try {
      const response = await Apis.getBrands();
      if (setBrands && response?.data) {
        setBrands(response.data);
      }
      return response;
    } catch (error) {
      return error;
    }
  };

  const updateBrand = async (brandId, data) => {
    try {
      const response = await Apis.updateBrand(brandId, data);
      return response;
    } catch (error) {
      return error;
    }
  };

  const deleteBrand = async (brandId) => {
    try {
      const response = await Apis.deleteBrand(brandId);
      return response;
    } catch (error) {
      return error;
    }
  };

  return {
    addBrand,
    getBrands,
    updateBrand,
    deleteBrand,
  };
};

// --- Hook from CarouselProducts/useHook.js ---
export const useBannerHook = () => {
  const addBanner = async (data) => {
    try {
      const response = await Apis.addBanner(data);
      return response;
    } catch (error) {
      return error;
    }
  };

  const getBanners = async (setBanners) => {
    try {
      const response = await Apis.getBanners();
      if (setBanners && response?.data) {
        setBanners(response.data);
      }
      return response;
    } catch (error) {
      return error;
    }
  };

  const updateBanner = async (bannerId, data) => {
    try {
      // Ensure you have Apis.updateBanner in your Apis file
      const response = await Apis.updateBanner(bannerId, data);
      return response;
    } catch (error) {
      return error;
    }
  };

  const deleteBanner = async (bannerId) => {
    try {
      const response = await Apis.deleteBanner(bannerId);
      return response;
    } catch (error) {
      return error;
    }
  };

  return {
    addBanner,
    getBanners,
    updateBanner,
    deleteBanner,
  };
};

// --- Hook from Categories/CategoriesDetails/useHook.js ---
export const useProductDetails = () => {
    const getProductDetails = async (id, setProductDetails) => {
        try {
            const response = await Apis.getProductDetails(id);
            setProductDetails(response.data);
            return response;
        } catch (error) {
            console.error("Error fetching product details: ", error);
            return error;
        }
    }
    return { getProductDetails };
};

// --- Hook from Categories/CategoriesList/useHook.js ---
export const useCategory = () => {
  const [loading, setLoading] = useState(false);

  const addCategory = async (categoryData) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("name", categoryData.name);
      formData.append("status", categoryData.status);

      if (categoryData.image) {
        formData.append("image", categoryData.image);
      }

      const response = await Apis.addCategory(formData);
      if (response?.status === 201) {
        return response.data;
      } else {
        throw new Error(response?.data?.message || "Failed to add category.");
      }
    } catch (error) {
      throw new Error(error.message || "Error adding category.");
    } finally {
      setLoading(false);
    }
  };
  const editCategory = async (id, categoryData) => {
    try {
      const formData = new FormData();
      formData.append("name", categoryData.name);
      formData.append("status", categoryData.status);

      if (categoryData.image instanceof File) {
        formData.append("image", categoryData.image);
      }

      for (const [key, value] of formData.entries()) {
      }


      const response = await Apis.updateCategoryData(id, formData);
      if (response?.status === 200) {
        return response.data;
      } else {
        throw new Error(response?.data?.message || "Failed to edit category.");
      }
    } catch (error) {
      throw new Error(error.message || "Error editing category.");
    }
  };
  const getCategories = async (setCategories) => {
    setLoading(true);
    try {
      const response = await Apis.getCategories();
      if (response?.status === 200) {
        setCategories(response.data.categories);
        return response;
      } else {
        throw new Error(response?.data?.message || "Failed to fetch categories.");
      }
    } catch (error) {
      alert(error.message || "Error fetching categories.");
    } finally {
      setLoading(false);
    }
  };

  const getCategoriesWithPagination = async (setCategories, setTotal, offset = 0, limit = 10, categoryName = "", status = "") => {
    setLoading(true);
    try {
      const params = {};

      if (categoryName) {
        params.categoryName = categoryName;
      }

      if (status !== "") {
        params.status = status === "true" ? true : false;
      }

      const response = await Apis.getCategoriesWithPagination(offset, limit, params);

      if (response?.status === 200) {
        setCategories(response.data.categories);
        const totalRecords = response.data.pagination?.totalRecords || response.data.categories.length;
        setTotal(totalRecords);
        return response;
      } else {
        throw new Error(response?.data?.message || "Failed to fetch categories.");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      throw new Error(error.message || "Error fetching categories.");
    } finally {
      setLoading(false);
    }
  };

  const searchCategories = async (categoryName, status, offset = 0, limit = 10) => {
    try {
      const params = {};

      if (categoryName) {
        params.categoryName = categoryName;
      }

      if (status !== "") {
        params.status = status === "true" ? true : false;
      }

      const response = await Apis.getCategoriesWithPagination(offset, limit, params);
      return response.data;
    } catch (error) {
      console.error("🚀 ~ file: useHook.js ~ line 64 ~ searchCategories ~ error", error);
      return error;
    }
  };

  const updateCategoryStatus = async (id, status) => {
    try {
      setLoading(true);
      const data = { id, status };
      const response = await Apis.updateCategoryStatus(id, data);
      if (response?.status === 200) {
        return response.data;
      } else {
        throw new Error(response?.data?.message || "Failed to update category status.");
      }
    } catch (error) {
      throw new Error(error.message || "Error updating category status.");
    } finally {
      setLoading(false);
    }
  };

  const deleteCategory = async (id) => {
    try {
      setLoading(true);
      const response = await Apis.deleteCategory(id);
      return response.data;
    } catch (error) {
      throw new Error(error.message || "Error deleting category.");
    } finally {
      setLoading(false);
    }
  };

  const updateCategoryPosition = async (categoryId, newPosition) => {
    try {
      const response = await Apis.updateCategoryPosition({ categoryId, newPosition });
      return response?.data;
    } catch (error) {
      console.error("Error updating category position:", error);
      throw error;
    }
  };

  return {
    addCategory,
    editCategory,
    loading,
    getCategories,
    getCategoriesWithPagination,
    updateCategoryStatus,
    searchCategories,
    deleteCategory,
    updateCategoryPosition
  };
};

// --- Hook from Categories/CategoriesView/useHook.js ---
export const useCategoryProducts = () => {
    const getCategoryProducts = async (id, setCategoryProducts) => {
        try {
            const response = await Apis.getCategoryProducts(id);
            if (response && response.data && Array.isArray(response.data.products)) {
                setCategoryProducts(response.data.products);
            } else {
                console.error("Invalid response format:", response);
                setCategoryProducts([]);
            }
        } catch (error) {
            console.error("Error in Hook:", error.message || error);
            setCategoryProducts([]);
        }
    };

    // New paginated version of getCategoryProducts
    const getCategoryProductsWithPagination = async (id, setCategoryProducts, setTotal, offset = 0, limit = 10, name = "", status = "") => {
        try {
            const params = {};
            
            if (name) {
                params.name = name;
            }
            
            if (status !== "") {
                params.status = status === "true" ? true : false;
            }

            const response = await Apis.getCategoryProductsWithPagination(id, offset, limit, params);
            if (response?.status === 200) {
                setCategoryProducts(response.data.products);
                // Extract total from pagination object
                const totalRecords = response.data.pagination?.totalRecords || response.data.products.length;
                setTotal(totalRecords);
                return response;
            } else {
                throw new Error(response?.data?.message || "Failed to fetch category products.");
            }
        } catch (error) {
            console.error("Error fetching category products:", error);
            throw new Error(error.message || "Error fetching category products.");
        }
    };

    const searchCategoryProducts = async (id, name, status, offset = 0, limit = 10) => {
        try {
            const params = {};
            
            if (name) {
                params.name = name;
            }
            
            if (status !== "") {
                params.status = status === "true" ? true : false;
            }

            const response = await Apis.getCategoryProductsWithPagination(id, offset, limit, params);
            return response.data;
        } catch (error) {
            console.error("🚀 ~ file: useHook.js ~ line 64 ~ searchCategoryProducts ~ error", error);
            return error;
        }
    };

    return {
        getCategoryProducts,
        getCategoryProductsWithPagination,
        searchCategoryProducts
    };
};

// --- Hook from DeliveryManagement/useHook.js ---
export const useDeliveryManagement = () => {
    const getInStationZones = async (setZoneData, offset = 0, limit = 9) => {
        try {
            const response = await Apis.getAllInStationZone(offset, limit);
            setZoneData(response.data.data);
            return response.data;
        } catch (error) {
            console.error('Error fetching zones:', error);
            throw error;
        }
    };
    const getOutStationZones = async (setZoneData, offset = 0, limit = 9) => {
        try {
            const response = await Apis.getAllZone(offset, limit);
            setZoneData(response.data.zones);
            return response.data;
        } catch (error) {
            console.error('Error fetching zones:', error);
            throw error;
        }
    };
    const getAllInStationZones = async (setAllZoneData) => {
        try {
            const response = await Apis.getAllInStationZones();
            setAllZoneData(response.data.instationZones);
            return response.data;
        } catch (error) {
            console.error('Error fetching zone:', error);
            throw error;
        }
    };
    const getAllOutStationZones = async (setAllZoneData) => {
        try {
            const response = await Apis.getZones();
            setAllZoneData(response.data.zones);
            return response.data;
        } catch (error) {
            console.error('Error fetching zone:', error);
            throw error;
        }
    };
    const getAllInStationBaseRates = async (setAllInStationBaseRatesData) => {
        try {
            const response = await Apis.getAllInStation();
            setAllInStationBaseRatesData(response.data.inStationBaseRates);
            return response.data;
        } catch (error) {
            console.error('Error fetching in station base rates:', error);
            throw error;
        }
    };
    const addInStationZone = async (data) => {
        try {
            const response = await Apis.createInStationZone(data);
            return response;
        } catch (error) {
            console.error('Error creating zone:', error);
            throw new Error('Failed to add zone');
        }
    };
    const addOutStationZone = async (data) => {
        try {
            const response = await Apis.createZone(data);
            return response;
        } catch (error) {
            console.error('Error creating zone:', error);
            throw error; // Pass through the original error to preserve backend message
        }
    };
    const updateInStationZone = async (id, data) => {
        try {
            const response = await Apis.updateInStationZone(id, data);
            return response;
        } catch (error) {
            console.error(error);
            throw new Error('Failed to update zone');
        }
    };
    const updateOutStationZone = async (id, data) => {
        try {
            const response = await Apis.updateZone(id, data);
            return response;
        } catch (error) {
            console.error(error);
            throw error; // Pass through the original error to preserve backend message
        }
    };
    const deleteInStationZone = async (id) => {
        try {
            const response = await Apis.deleteInStationZone(id);
            return response;
        } catch (error) {
            console.error(error);
            throw new Error('Failed to delete zone');
        }
    };
    const deleteOutStationZone = async (id) => {
        try {
            const response = await Apis.deleteZone(id);
            return response;
        } catch (error) {
            console.error(error);
            throw new Error('Failed to delete zone');
        }
    };
    const addCities = async (data) => {
        try {
            const response = await Apis.createCity(data);
            return response;
        } catch (error) {
            console.error(error);
            throw new Error('Failed to add city');
        }
    };
    const getCities = async (setCities, offset = 0, limit = 9) => {
        try {
            const response = await Apis.getCities(offset, limit);
            setCities(response.data.data);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const deleteCity = async (id) => {
        try {
            const response = await Apis.deleteCity(id);
            return response;
        } catch (error) {
            console.error(error);
            throw new Error('Failed to delete city');
        }
    };
    const updateCity = (id, data) => {
        try {
            const response = Apis.updateCityStatus(id, data);
            return response;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const getInStationBaseRates = async (setInStationBaseRatesData, offset = 0, limit = 9) => {
        try {
            const response = await Apis.getAllInStation(offset, limit);
            setInStationBaseRatesData(response.data.inStationBaseRates || []);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const getOutStationBaseRates = async (setOutStationBaseRatesData, offset = 0, limit = 9) => {
        try {
            const response = await Apis.getAllOutStationBaseRates(offset, limit);
            setOutStationBaseRatesData(response.data.outStationBaseRates || []);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const addInStationBaseRate = async (data) => {
        try {
            const response = await Apis.createInStationBaseRate(data);
            return response;
        } catch (error) {
            console.error(error);
            throw error;
        };
    }
    const addOutStationBaseRate = async (data) => {
        try {
            const response = await Apis.createOutStationBaseRate(data);
            return response;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const updateInStationBaseRate = async (id, data) => {
        try {
            const response = await Apis.updateInStationBaseRate(id, data);
            return response;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const updateOutStationBaseRate = async (id, data) => {
        try {
            const response = await Apis.updateOutStationBaseRate(id, data);
            return response;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };
    const deleteInStationBaseRate = async (id) => {
        try {
            const response = await Apis.deleteInStationBaseRate(id);
            return response;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const deleteOutStationBaseRate = async (id) => {
        try {
            const response = await Apis.deleteOutStationBaseRate(id);
            return response;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    return {
        addCities,
        getCities,
        updateCity,
        deleteCity,
        getInStationZones,
        getAllInStationZones,
        addInStationZone,
        updateInStationZone,
        deleteInStationZone,
        getOutStationZones,
        getAllOutStationZones,
        addOutStationZone,
        updateOutStationZone,
        deleteOutStationZone,
        getInStationBaseRates,
        getOutStationBaseRates,
        addInStationBaseRate,
        addOutStationBaseRate,
        updateInStationBaseRate,
        updateOutStationBaseRate,
        deleteInStationBaseRate,
        deleteOutStationBaseRate,
        getAllInStationBaseRates
    };
};

// --- Hook from Orders/VerifyPayments/useHook.js ---
export const useOrderPayments = () => {
  const getPayment = async (status) => {
    try {
      const response = await Apis.getPayments(status);
      return response;
    } catch (error) {
      console.error("Error fetching payment data:", error);
      return error;
    }
  };

  const verifyPayment = async (id, status) => {
    try {
      const body = { paymentStatus: status };
      const response = await Apis.updatePaymentStatus(id, body);
      return response;
    } catch (error) {
      return error;
    }
  };

  return { verifyPayment, getPayment };
};

// --- Hook from ProductDeliveryCharges/useHook.js ---
export const useDeliveryCharges = () => {
    const getCities = async (setCities) => {
        try {
            const response = await Apis.getCities();
            setCities(response.data.data);
        } catch (error) {
            console.error(error);
        }
    };
    const getProducts = async (setProducts) => {
        try {
            const response = await Apis.getProductsForDeliveryCharges();
            if (response && response.data && Array.isArray(response.data.products)) {
                setProducts(response.data.products);
            } else {
                setProducts([]);
            }
        } catch (error) {
            console.error(error);
            setProducts([]);
        }
    };
    const getProductNameList = async (setProducts) => {
        try {
            const response = await Apis.getProductNameList();
            setProducts(response.data.data);
        } catch (error) {
            console.error(error);
        }
    }
    const addProductDeliveryCharges = async (data) => {
        try {
            const response = await Apis.addProductDeliveryCharges(data);
            return response;
        } catch (error) {
            console.error(error);
        }
    }
    const getProductDeliveryCharges = async (setDeliveryCharges, offset = 0, limit = 9) => {
        try {
            const response = await Apis.getProductsDeliveryCharges(offset, limit);
            setDeliveryCharges(response.data.data);
            return response.data;
        } catch (error) {
            console.error(error);
            setDeliveryCharges([]);
        }
    };
    const deleteProductDeliveryCharges = async (id) => {
        try {
            const response = await Apis.deleteProductDeliveryCharges(id);
            return response;
        } catch (error) {
            console.error(error);
        }
    }
    const editProductDeliveryCharges = async (id, data) => {
        try {
            const response = await Apis.updateProductDeliveryCharges(id, data);
            return response;
        } catch (error) {
            console.error(error);
        }
    }
    return { getCities, getProducts, getProductDeliveryCharges, addProductDeliveryCharges, deleteProductDeliveryCharges, editProductDeliveryCharges, getProductNameList };
}

// --- Hook from Products/ProductList/useHook.js ---
export const useProducts = () => {
    const [loading, setLoading] = useState(false);
    const getProducts = async (setProducts) => {
        try {
            const response = await Apis.getProducts();
            if (response && response.data && Array.isArray(response.data.products)) {
                setProducts(response.data.products);
            } else {
                console.error("Invalid response format:", response);
            }
        } catch (error) {
            console.error("Error in Hook:", error.message || error);
        }
    };

    const getProductsWithPagination = async (setProducts, setTotal, offset = 0, limit = 20, searchQuery = "", statusFilter = "", categoryName = "") => {
        setLoading(true);
        try {
            const params = {};

            if (statusFilter !== "") {
                params.status = statusFilter === "true" ? true : false;
            }

            if (searchQuery && searchQuery.trim()) {
                params.name = searchQuery.trim();
            }

            if (categoryName && categoryName.trim()) {
                params.categoryName = categoryName.trim();
            }

            const response = await Apis.getProductsWithPagination(offset, limit, params);
            if (response?.status === 200) {
                setProducts(response.data.products);
                const totalRecords = response.data.pagination?.totalRecords || response.data.products.length;
                setTotal(totalRecords);
                return response.data;
            } else {
                throw new Error(response?.data?.message || "Failed to fetch products.");
            }
        } catch (error) {
            console.error("Error fetching products:", error);
            throw new Error(error.message || "Error fetching products.");
        } finally {
            setLoading(false);
        }
    };

    const getCategories = async (setCategories) => {
        try {
            const response = await Apis.getCategoriesToAddProducts();
            if (
                response &&
                response.data &&
                Array.isArray(response.data.categories)
            ) {
                setCategories(response.data.categories);
            } else {
                console.error("Invalid response format:", response);
                setCategories([]);
            }
        } catch (error) {
            console.error("Error fetching categories:", error?.message || error);
            setCategories([]);
        }
    };
    const updateProductStatus = async (id, status) => {
        try {
            setLoading(true);
            const requestBody = {
                id,
                status,
            };
            const response = await Apis.updateProductStatus(requestBody);
            if (response && response.data) {
                return response;
            } else {
                console.error("Invalid response format:", response);
                return null;
            }
        } catch (error) {
            console.error("Error in Hook:", error.message || error);
            return null;
        } finally {
            setLoading(false);
        }
    };
    const deleteProduct = async (id) => {
        try {
            const response = await Apis.deletProduct(id);
            if (response && response.data) {
                return response;
            } else {
                console.error("Invalid response format:", response);
                return null;
            }
        } catch (error) {
            console.error("Error in Hook:", error.message || error);
            return null;
        }
    };
    const addProduct = async (formData) => {
        try {
            const response = await Apis.addProduct(formData);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error; // Re-throw the error so the component can handle it
        }
    };
    const updateProduct = async (updateId, data) => {
        try {
            const response = await Apis.updateProduct(updateId, data);
        } catch (error) {
            console.error(error);
        }
    };
    const getBrands = async (setBrandsData) => {
        try {
            const response = await Apis.getBrands();
            setBrandsData(response.data);
            return response.data;
        } catch (error) {
            throw error;
        }
    };

    const updateProductPosition = async (productId, newPosition) => {
        try {
            const response = await Apis.updateProductPosition(productId, newPosition);
            return response?.data ?? response;
        } catch (error) {
            console.error("Error in Hook:", error.message || error);
            throw error;
        }
    };

    return {
        getProducts,
        getProductsWithPagination,
        getCategories,
        updateProductStatus,
        deleteProduct,
        addProduct,
        updateProduct,
        getBrands,
        updateProductPosition
    };
};

// --- Hook from Products/ProductView/useHook.js ---
export const useDiscountTier = () => {
    const getDiscountTier = async (productId, setDiscountTiers) => {
        try {
            const response = await Apis.getDiscountedProducts(productId)
            setDiscountTiers(response.data.data)
            return response
        } catch (error) {
            return error
        }
    }
    const addDiscountTier = async (productId, newDiscount) => {
        try {
            const payload = {
                productId,
                discountTiers: [newDiscount]
            };

            const response = await Apis.addDiscountedProducts(payload);

            return response.data.data.discountTiers;
        } catch (error) {
            return error;
        }
    };
    const delteDiscountTier = async (productId, discountTierId) => {
        try {
            const response = await Apis.deleteDiscountedProducts(productId, discountTierId);
            return response;
        } catch (error) {
            return error;
        }
    }
    const showOnHomeScreen = async (productId, homePage) => {
        try {
            const body = {
                productId,
                homePage: homePage === 1 ? 0 : 1,
            };
            const response = await Apis.showOnHomeScreen(body);

            if (response?.message === "Success") {
                Swal.fire({
                    title: "Success!",
                    text: `The product has been successfully ${homePage === 1 ? "removed from" : "added to"} the home screen.`,
                    icon: "success",
                    confirmButtonText: "OK",
                });
            }

            return response;
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: "There was an issue updating the product on the home screen.",
                icon: "error",
                confirmButtonText: "OK",
            });
            return error;
        }
    };
    const getProductData = async (productId, setProductData) => {
        try {
            const response = await Apis.getProductById(productId);
            setProductData(response.data);
            return response.data.homeScreen;
        } catch (error) {
            return error;
        }
    }
    const addStock = async (productId, stock) => {
        try {
            const payload = {
                productId,
                stock
            }
            const response = await Apis.addStock(payload);
            if (response?.status === 200) {
                Swal.fire({
                    title: "Success!",
                    text: "Stock has been added successfully.",
                    icon: "success",
                    confirmButtonText: "OK",
                });
            }
            return response
        } catch (error) {
            return error
        }
    }
    const showProductOnHomeScreen = async (productId, homePage) => {
        try {
            const data = {
                productId,
                homePage: homePage ? 1 : 0
            };
            const response = await Apis.showOnHomeScreen(data);
            return response;
        } catch (error) {
            console.error("Error updating home screen status:", error);
            return error;
        }
    };
    const getCategories = async (setCategories) => {
        try {
            const response = await Apis.getCategoriesToAddProducts();
            setCategories(response.data.categories);
        } catch (error) {
            return error
        }
    }

    const updateVariantStock = async (colorId, inStock) => {
        try {
            const response = await Apis.updateVariantStock({ colorId, inStock });
            if (response?.status === 200 || response?.data?.status || response?.data?.message) {
                Swal.fire({
                    title: "Success!",
                    text: response?.data?.message || "Variant stock status updated successfully.",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false,
                });
            }
            return response;
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: error.response?.data?.error || error.response?.data?.message || "Failed to update variant stock status.",
                icon: "error",
                timer: 2000,
                showConfirmButton: false,
            });
            return error;
        }
    }

    const updateVariantStockDetails = async (data) => {
        try {
            // Use the new color-level stock API endpoint
            const response = await Apis.updateVariantStock(data);
            if (response?.status === 200 || response?.data?.status || response?.data?.message) {
                Swal.fire({
                    title: "Success!",
                    text: response?.data?.message || "Variant stock details updated successfully.",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false,
                });
            }
            return response;
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: error.response?.data?.error || error.response?.data?.message || "Failed to update variant stock details.",
                icon: "error",
                timer: 3000,
                showConfirmButton: false,
            });
            return error;
        }
    }

    const updateMultipleVariantStock = async (variants) => {
        try {
            const response = await Apis.updateMultipleVariantStock({ variants });
            if (response?.status === 200 || response?.data?.message) {
                Swal.fire({
                    title: "Success!",
                    text: "All variant stock statuses updated successfully.",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false,
                });
            }
            return response;
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: error.response?.data?.message || "Failed to update variant stock statuses.",
                icon: "error",
                timer: 2000,
                showConfirmButton: false,
            });
            return error;
        }
    }

    return { getDiscountTier, addDiscountTier, delteDiscountTier, showOnHomeScreen, getProductData, addStock, showProductOnHomeScreen, getCategories, updateVariantStock, updateVariantStockDetails, updateMultipleVariantStock };
}

// --- Hook from Quotation/useHook.js ---
export const useQuotation = () => {
    const getProducts = async (setProductList) => {
        try {
            const response = await Apis.getAllProducts();
            setProductList(response.data.products);
        } catch (error) {
            console.error(error);
        }
    }
    const generatePdf = async (data, setPdfFileLink) => {
        const response = await Apis.generatePdf(data);
        const fileUrl = response?.data?.fileUrl;
        if (setPdfFileLink && fileUrl) setPdfFileLink(fileUrl);
        return fileUrl;
    }
    const getVarinatByProductId = async (productId) => {
        try {
            const response = await Apis.getVarinatByProductId(productId);
            return response.data;
        } catch (error) {
            console.error(error);
        }
    }
    const getColorsByVariantId = async (variantId) => {
        try {
            const response = await Apis.getColorsByVariantId(variantId);
            return response.data;
        } catch (error) {
            console.error(error);
        }
    }
    return {
        getProducts,
        generatePdf,
        getVarinatByProductId,
        getColorsByVariantId,
    }
}

// --- Hook from Reviews/useHook.js ---
export const useReviews = () => {
    const getReviews = async (status, setReviewData, offset = 0, limit = 9) => {
        try {
            const params = {
                status,
                offset,
                limit
            };
            const response = await Apis.getReviews(params);
            setReviewData(response.data);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const acceptReviews = async (body) => {
        try {
            const response = await Apis.acceptReviews(body);
            return response;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    return { getReviews, acceptReviews };
};

// --- Combined Hook for Orders ---
export const useOrders = () => {
    const getORders = async (setOrderData, params = {}, offset = 0, limit = 9) => {
        try {
            const requestParams = {
                ...params,
                offset,
                limit
            };
            const response = await Apis.getOrders(requestParams);
            setOrderData(response.data.ordersData);
            return response.data;
        } catch (error) {
            console.error("Error fetching orders:", error);
            throw error;
        }
    }

    const updateOrderStatus = async (orderId, status) => {
        try {
            const body = { status };
            const response = await Apis.updateOrderStatus(orderId, body);
            return response;
        } catch (error) {
            console.error("Error updating order status:", error);
            throw error;
        }
    }

    const getOrderByOrderId = async (orderId, setOrderData) => {
        try {
            const response = await Apis.getOrderByOrderId(orderId);
            const orderData = response.data?.orderData || response.orderData || response.data;
            setOrderData(orderData);
        } catch (error) {
            console.error("Error fetching order data:", error);
        }
    };

    return { getORders, updateOrderStatus, getOrderByOrderId };
}
